import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { AGENT_LINKS } from './links';
import { agentApi, publicApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { Loader } from '@/components/Loader';

export default function AgentBailleurDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await agentApi.getBailleur(id); setData(r.data); }
    catch (err) {
      console.debug('[AgentBailleurDetail] load failed:', err?.response?.status);
      toast.error('Bailleur introuvable');
      nav('/agent/bailleurs');
    }
    finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    publicApi.offers()
      .then(({ data: catalogue }) => setOffers(catalogue.items || []))
      .catch(() => toast.error('Impossible de charger le catalogue des offres.'));
  }, []);

  const changeOffer = async (offer) => {
    await agentApi.updateSubscription(id, { offer });
    toast.success(`Offre mise à jour : ${offer}`);
    load();
  };

  return (
    <AppLayout links={AGENT_LINKS} testid={TID.agentSidebar} title={loading ? '' : `${data.bailleur.prenom} ${data.bailleur.nom}`}>
      <button onClick={() => nav('/agent/bailleurs')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0D1B2A] -mt-4 mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour au portefeuille
      </button>
      {loading || !data ? null : (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="lb-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Profil bailleur</div>
                  <div className="mt-1 text-xl font-bold text-[#0D1B2A]">{data.bailleur.prenom} {data.bailleur.nom}</div>
                  <div className="text-sm text-slate-500">{data.bailleur.email} · {data.bailleur.telephone}</div>
                </div>
                {data.bailleur.is_active ? (
                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">Actif</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Inactif</Badge>
                )}
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                <div><div className="text-xs text-slate-500">Adresse</div><div className="text-[#0D1B2A]">{data.bailleur.adresse || '—'}</div></div>
                <div><div className="text-xs text-slate-500">Ville / Pays</div><div className="text-[#0D1B2A]">{data.bailleur.ville || '—'} · {data.bailleur.pays}</div></div>
                <div><div className="text-xs text-slate-500">Créé le</div><div className="text-[#0D1B2A]">{new Date(data.bailleur.created_at).toLocaleDateString('fr-FR')}</div></div>
                <div><div className="text-xs text-slate-500">Nb locataires</div><div className="text-[#0D1B2A] font-semibold">{data.locataires_count} / {data.subscription?.max_locataires || 0}</div></div>
              </div>
            </div>

            <div className="lb-card p-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Locataires ({data.locataires.length})</div>
              {data.locataires.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Ce bailleur n'a pas encore ajouté de locataire.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.locataires.map((l) => (
                    <div key={l.id} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-[#0D1B2A]">{l.prenom} {l.nom}</div>
                        <div className="text-xs text-slate-500">{l.adresse_logement}</div>
                      </div>
                      <div className="text-sm font-bold text-[#0D1B2A]">{l.montant_loyer} {l.devise}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="lb-card p-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Abonnement</div>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-[#0D1B2A] capitalize">{data.subscription?.offer}</div>
                  <div className="text-xs text-slate-500">Max {data.subscription?.max_locataires} locataires</div>
                </div>
                <Badge className="bg-[#E6F1FF] text-[#1677FF] hover:bg-[#E6F1FF]">${data.subscription?.price_usd}/an</Badge>
              </div>
              <div className="mt-4">
                <div className="text-xs text-slate-500 mb-1.5">Changer l'offre</div>
                <Select value={data.subscription?.offer} onValueChange={changeOffer}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{offers.map((offer) => (
                    <SelectItem key={offer.code} value={offer.code}>
                      {offer.nom} · {offer.locataires_max} loc. · {Number(offer.prix_annuel_usd || 0) === 0 ? 'Gratuit' : `${offer.prix_annuel_usd} $/an`}
                    </SelectItem>
                  ))}</SelectContent>
                </Select>
              </div>
              <div className="mt-4 text-xs text-slate-500">
                Statut : <span className="font-semibold text-[#0D1B2A] capitalize">{data.subscription?.status}</span><br />
                Expire le : {data.subscription?.expires_at ? new Date(data.subscription.expires_at).toLocaleDateString('fr-FR') : '—'}
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
